import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";

type ScrollDirection = "up" | "down" | undefined;

interface UseScrollDirectionOptions {
  disabled?: boolean;
  tolerance?: number;
  topThreshold?: number;
  bottomThreshold?: number;
}

const useScrollDirection = ({
  disabled = false,
  tolerance = 5,
  topThreshold = 8,
  bottomThreshold = 8,
}: UseScrollDirectionOptions = {}) => {
  const pathname = usePathname();
  const [scrollDirection, setScrollDirection] = useState<ScrollDirection>();
  const lastScrollYRef = useRef(0);
  const isTickingRef = useRef(false);

  const getScrollY = () => {
    const scrollingElement =
      document.scrollingElement || document.documentElement;
    if (scrollingElement) {
      return scrollingElement.scrollTop;
    }
    if (typeof window !== "undefined") {
      return window?.scrollY ? window.scrollY : 0;
    }
    return 0;
  };

  const getMaxScrollY = () => {
    const scrollingElement =
      document.scrollingElement || document.documentElement;
    const scrollHeight = scrollingElement
      ? scrollingElement.scrollHeight
      : document.body.scrollHeight;
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    return Math.max(0, scrollHeight - viewportHeight);
  };

  useEffect(() => {
    setScrollDirection(undefined);
  }, [pathname]);

  useEffect(() => {
    if (disabled) {
      return;
    }

    lastScrollYRef.current = getScrollY();

    const updateScrollDirection = () => {
      const currentY = getScrollY();
      const deltaY = currentY - lastScrollYRef.current;
      const isAtTop = currentY <= topThreshold;
      const isAtBottom = currentY >= getMaxScrollY() - bottomThreshold;

      if (
        Math.abs(deltaY) > tolerance &&
        !(deltaY > 0 && isAtTop) &&
        !(deltaY < 0 && isAtBottom)
      ) {
        setScrollDirection(deltaY > 0 ? "down" : "up");
        lastScrollYRef.current = currentY;
      } else {
        lastScrollYRef.current = currentY;
      }

      isTickingRef.current = false;
    };

    const handleScroll = () => {
      if (!isTickingRef.current) {
        isTickingRef.current = true;
        requestAnimationFrame(updateScrollDirection);
      }
    };

    const resetScrollBaseline = () => {
      lastScrollYRef.current = getScrollY();
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", resetScrollBaseline);
    window.visualViewport?.addEventListener("resize", resetScrollBaseline);
    window.addEventListener("orientationchange", resetScrollBaseline);

    requestAnimationFrame(resetScrollBaseline);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", resetScrollBaseline);
      window.visualViewport?.removeEventListener("resize", resetScrollBaseline);
      window.removeEventListener("orientationchange", resetScrollBaseline);
    };
  }, [disabled, tolerance, topThreshold, bottomThreshold]);

  return { direction: scrollDirection };
};

export default useScrollDirection;
